import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  IconButton,
} from '@mui/material';
import {
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsHockey as IceHockeyIcon,
  SportsTennis as TennisIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  Sports as SportsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  School as LearningIcon,
  WorkspacePremium as CertificateIcon,
  Description as DescriptionIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { CORE_SPORTS, getDefaultRankingSubcategory } from '@utils/sports';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { loadPlayerProfileForUser, buildFallbackPlayerProfile } from '@utils/coachPlayerProfile';

const DRAWER_WIDTH = 280;

// Helper function to get sport icon
const getSportIcon = (sport: string) => {
  switch (sport.toLowerCase()) {
    case 'badminton':
      return <BadmintonIcon sx={{ fontSize: 40 }} />;
    case 'cricket':
      return <CricketIcon sx={{ fontSize: 40 }} />;
    case 'tennis':
      return <TennisIcon sx={{ fontSize: 40 }} />;
    case 'football':
      return <FootballIcon sx={{ fontSize: 40 }} />;
    case 'ice hockey':
      return <IceHockeyIcon sx={{ fontSize: 40 }} />;
    case 'athletics':
      return <AthleticsIcon sx={{ fontSize: 40 }} />;
    default:
      return <SportsIcon sx={{ fontSize: 40 }} />;
  }
};

// Helper function to get grade color
const getGradeColor = (grade: string) => {
  switch (grade) {
    case 'A+':
      return '#4caf50';
    case 'A':
      return '#2196f3';
    case 'B':
      return '#ff9800';
    case 'C':
      return '#f44336';
    default:
      return '#757575';
  }
};

const getSportTheme = (sport: string) => {
  switch (sport.toLowerCase()) {
    case 'badminton':
      return { main: '#1565c0', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 60%, #dbeafe 100%)' };
    case 'cricket':
      return { main: '#2e7d32', light: '#e8f5e9', border: '#a5d6a7', gradient: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 60%, #dcfce7 100%)' };
    case 'tennis':
      return { main: '#827717', light: '#f9fbe7', border: '#dce775', gradient: 'linear-gradient(135deg, #f9fbe7 0%, #ffffff 60%, #fef9c3 100%)' };
    case 'football':
      return { main: '#00897b', light: '#e0f2f1', border: '#80cbc4', gradient: 'linear-gradient(135deg, #e0f2f1 0%, #ffffff 60%, #ccfbf1 100%)' };
    case 'ice hockey':
      return { main: '#5e35b1', light: '#ede7f6', border: '#b39ddb', gradient: 'linear-gradient(135deg, #ede7f6 0%, #ffffff 60%, #e9d5ff 100%)' };
    case 'athletics':
      return { main: '#ef6c00', light: '#fff3e0', border: '#ffcc80', gradient: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 60%, #ffedd5 100%)' };
    default:
      return { main: '#1976d2', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 60%, #dbeafe 100%)' };
  }
};

interface SportMetrics {
  sport: string;
  totalEvaluations: number;
  selfEvaluationScore?: number;
  coachEvaluationScore?: number;
  practicalTestScore?: number;
  bestGrade?: string;
  lastEvaluationDate?: string;
  hasData: boolean;
}

interface PlayerRankingOverviewPageProps {
  portalMode?: PortalMode;
}

const PlayerRankingOverviewPage: React.FC<PlayerRankingOverviewPageProps> = ({ portalMode = 'player' }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [player, setPlayer] = useState<any>(null);
  const [sportMetrics, setSportMetrics] = useState<SportMetrics[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Available sports (can be fetched from API later)
  const availableSports = CORE_SPORTS;

  useEffect(() => {
    loadPlayerDetails();
  }, [user?.id]);

  useEffect(() => {
    if (player?.id) {
      // Load metrics so we show correct state (View Details vs Start Evaluation) without flashing
      loadSportMetrics();
    }
  }, [player?.id]);

  const loadPlayerDetails = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const profile = await loadPlayerProfileForUser(
        user.id,
        token,
        portalMode === 'coach',
        user
      );
      if (profile) {
        setPlayer(profile);
      } else {
        setError('Failed to load player profile');
      }
    } catch (err: any) {
      console.error('Error loading player details:', err);
      if (portalMode === 'coach' && user) {
        setPlayer(buildFallbackPlayerProfile(user));
      } else {
        setError(err.message || 'Failed to load player details');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSportMetrics = async () => {
    if (!player?.id) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const metrics: SportMetrics[] = [];

      // For each sport, fetch ranking evaluation summaries
      for (const sport of availableSports) {
        try {
          const categories = ['self-evaluation', 'coach-academy', 'practical-test'];
          let totalEvaluations = 0;
          let selfScore: number | undefined;
          let coachScore: number | undefined;
          let practicalScore: number | undefined;
          let bestGrade: string | undefined;
          let lastDate: string | undefined;

          for (const categoryId of categories) {
            try {
              const response = await fetch(
                `/api/ranking-evaluations/summary/${categoryId}/player/${player.id}?sport=${encodeURIComponent(sport)}`,
                {
                  headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                  }
                }
              );

              if (response.ok) {
                const data = await response.json();
                if (data.success && data.data && data.data.totalStrokes > 0) {
                  totalEvaluations++;
                  const score = data.data.score || 0;
                  const grade = data.data.grade || 'C';
                  const evalDate = data.data.evaluationDate;

                  if (categoryId === 'self-evaluation') {
                    selfScore = score;
                  } else if (categoryId === 'coach-academy') {
                    coachScore = score;
                  } else if (categoryId === 'practical-test') {
                    practicalScore = score;
                  }

                  // Track best grade
                  if (!bestGrade || getGradeValue(grade) > getGradeValue(bestGrade)) {
                    bestGrade = grade;
                  }

                  // Track latest date
                  if (evalDate && (!lastDate || new Date(evalDate) > new Date(lastDate))) {
                    lastDate = evalDate;
                  }
                }
              }
            } catch (err) {
              console.error(`Error loading ${categoryId} for ${sport}:`, err);
            }
          }

          metrics.push({
            sport,
            totalEvaluations,
            selfEvaluationScore: selfScore,
            coachEvaluationScore: coachScore,
            practicalTestScore: practicalScore,
            bestGrade,
            lastEvaluationDate: lastDate,
            hasData: totalEvaluations > 0,
          });
        } catch (err) {
          console.error(`Error loading metrics for ${sport}:`, err);
          metrics.push({
            sport,
            totalEvaluations: 0,
            hasData: false,
          });
        }
      }

      // Filter to show sports with data first, then others
      const sortedMetrics = [
        ...metrics.filter(m => m.hasData),
        ...metrics.filter(m => !m.hasData),
      ];

      setSportMetrics(sortedMetrics);
    } catch (err: any) {
      console.error('Error loading sport metrics:', err);
      setError(err.message || 'Failed to load ranking metrics');
    } finally {
      setLoading(false);
    }
  };

  const getGradeValue = (grade: string): number => {
    const gradeMap: { [key: string]: number } = {
      'A+': 5,
      'A': 4,
      'B': 3,
      'C': 2,
      'D': 1,
    };
    return gradeMap[grade] || 0;
  };

  const handleSportClick = (sport: string) => {
    // Navigate with a concrete skill level (all-level is deprecated)
    const allowedLevels = ['beginner', 'intermediate', 'advanced', 'pro'];
    const playerLevel = String(player?.level || '').toLowerCase();
    const level = allowedLevels.includes(playerLevel) ? playerLevel : 'beginner';
    const subcategory = getDefaultRankingSubcategory(sport);
    navigate(
      paths.rankingEvaluation('self-evaluation', {
        sport,
        level,
        subcategory,
      })
    );
  };

  if (loading && !player) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !player) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const drawer = <PlayerSidebar />;

  const mainContent = (
        <Container maxWidth="lg" sx={{ mb: 4, pt: hideSidebar ? 2 : 0 }}>
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          color: '#fff',
          background: 'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.22), transparent 24%), linear-gradient(135deg, #0f4c81 0%, #1565c0 45%, #6d28d9 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 180, height: 180, right: -55, top: -65, borderRadius: '50%', border: '28px solid rgba(255,255,255,0.14)' }} />
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 950, position: 'relative' }}>
          <RankingIcon sx={{ fontSize: 34, color: '#fff' }} />
          Learn and Rank
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.86)', maxWidth: 760, position: 'relative' }}>
          Continue structured learning, watch training videos, and rank skills, drills, and fitness for each sport.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sportMetrics.map((metric) => {
            const theme = getSportTheme(metric.sport);
            return (
            <Paper
              key={metric.sport}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor: theme.border,
                background: theme.gradient,
                boxShadow: metric.hasData ? `0 12px 28px ${theme.main}18` : 'none',
                overflow: 'hidden',
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  width: 120,
                  height: 120,
                  right: -42,
                  top: -52,
                  borderRadius: '50%',
                  border: `20px solid ${theme.main}16`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '260px 1fr auto' },
                  alignItems: 'center',
                  gap: 1.5,
                  position: 'relative',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 2.5,
                      bgcolor: theme.light,
                      color: theme.main,
                      border: `1px solid ${theme.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                      {getSportIcon(metric.sport)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 950, color: theme.main, lineHeight: 1.1 }}>
                      {metric.sport}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {metric.hasData ? `${metric.totalEvaluations}/3 evaluations completed` : 'Learning and ranking not started'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {[
                    { label: 'Self', value: metric.selfEvaluationScore, color: '#2e7d32' },
                    { label: 'Coach', value: metric.coachEvaluationScore, color: '#6d28d9' },
                    { label: 'EncycGames', value: metric.practicalTestScore, color: '#d97706' },
                  ].map((item) => (
                    <Chip
                      key={`${metric.sport}-${item.label}`}
                      label={`${item.label}: ${item.value !== undefined ? item.value.toFixed(1) : '-'}`}
                      size="small"
                      sx={{
                        bgcolor: item.value !== undefined ? `${item.color}18` : '#f1f5f9',
                        color: item.value !== undefined ? item.color : 'text.secondary',
                        border: `1px solid ${item.value !== undefined ? `${item.color}55` : '#e2e8f0'}`,
                        fontWeight: 900,
                      }}
                    />
                  ))}
                  {metric.bestGrade && (
                    <Chip
                      label={`Best Grade: ${metric.bestGrade}`}
                      size="small"
                      sx={{ bgcolor: `${getGradeColor(metric.bestGrade)}18`, color: getGradeColor(metric.bestGrade), fontWeight: 900 }}
                    />
                  )}
                  {metric.lastEvaluationDate && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, md: 1 } }}>
                      Last: {new Date(metric.lastEvaluationDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => handleSportClick(metric.sport)}
                  sx={{
                    justifySelf: { xs: 'stretch', md: 'end' },
                    bgcolor: theme.main,
                    color: '#fff',
                    borderRadius: 999,
                    px: 2.5,
                    fontWeight: 950,
                    textTransform: 'none',
                    boxShadow: `0 10px 22px ${theme.main}2e`,
                    '&:hover': { bgcolor: theme.main, filter: 'brightness(0.92)' },
                  }}
                >
                  Learn and Rank
                </Button>
              </Box>
            </Paper>
            );
          })}
        </Box>
      )}

      {/* Summary Statistics */}
      {sportMetrics.filter(m => m.hasData).length > 0 && (
        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: '#1976d2' }} />
            Summary Statistics
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Sports with Evaluations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {sportMetrics.filter(m => m.hasData).length}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Evaluations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {sportMetrics.reduce((sum, m) => sum + m.totalEvaluations, 0)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Average Grade
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {(() => {
                    const grades = sportMetrics
                      .filter(m => m.bestGrade)
                      .map(m => getGradeValue(m.bestGrade!));
                    if (grades.length === 0) return 'N/A';
                    const avg = grades.reduce((a, b) => a + b, 0) / grades.length;
                    const gradeMap: { [key: number]: string } = {
                      5: 'A+',
                      4: 'A',
                      3: 'B',
                      2: 'C',
                      1: 'D',
                    };
                    return gradeMap[Math.round(avg)] || 'C';
                  })()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
        </Container>
  );

  if (hideSidebar) {
    return mainContent;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        {mainContent}
      </Box>
    </Box>
  );
};

export default PlayerRankingOverviewPage;

